import React from "react";
import { motion } from "framer-motion";

import {
  FaReact,
  FaNodeJs,
  FaJava,
  FaPython,
  FaAws,
  FaDocker,
  FaBrain,
  FaDatabase,
  FaProjectDiagram,
} from "react-icons/fa";

import {
  SiSpringboot,
  SiMongodb,
  SiPostgresql,
  SiMysql,
  SiRedis,
  SiKubernetes,
  SiDotnet,
  SiLeetcode,
  SiCodeforces,
  SiCodechef,
  SiGeeksforgeeks,
  SiLangchain,
} from "react-icons/si";

const cards = [
  {
    title: "Backend & Systems Engineering",
    desc: "Scalable services, clean architecture, and data consistency.",
    points: ["REST APIs & auth", "Performance & caching", "Microservices"],
    icons: [
      <FaJava className="text-orange-500" />,
      <SiSpringboot className="text-green-500" />,
      <FaNodeJs className="text-green-500" />,
      <SiRedis className="text-red-400" />,
    ],
  },
  {
    title: "Full-Stack Engineering",
    desc: "End-to-end applications, from UI to API.",
    points: ["Frontend-backend integration", "Reusable components", "UX-focused development"],
    icons: [
      <FaReact className="text-cyan-500" />,
      <FaNodeJs className="text-green-500" />,
      <SiDotnet className="text-purple-500" />,
    ],
  },
  {
    title: "Cloud & DevOps",
    desc: "Cloud-native infrastructure and automated deployments.",
    points: ["CI/CD pipelines", "Container orchestration", "AWS deployments"],
    icons: [
      <FaAws className="text-orange-400" />,
      <FaDocker className="text-blue-500" />,
      <SiKubernetes className="text-blue-400" />,
    ],
  },
  {
    title: "Databases & Data Modeling",
    desc: "Efficient data models for relational and NoSQL stores.",
    points: ["Schema design", "SQL/NoSQL trade-offs", "Data integrity"],
    icons: [
      <FaDatabase className="text-slate-500" />,
      <SiPostgresql className="text-blue-400" />,
      <SiMysql className="text-sky-500" />,
      <SiMongodb className="text-green-500" />,
    ],
  },
  {
    title: "System Design (LLD & HLD)",
    desc: "Class-level design and distributed architecture for scale.",
    points: ["SOLID & design patterns", "Service decomposition", "Scalability & reliability"],
    icons: [
      <FaProjectDiagram className="text-indigo-500" />,
      <FaJava className="text-orange-500" />,
      <FaAws className="text-orange-400" />,
    ],
  },
  {
    title: "AI & LLM Engineering",
    desc: "Retrieval, agents, and model integration for real products.",
    points: ["RAG & vector search", "Tool-using agents", "LLM evaluation & cost control"],
    icons: [
      <FaBrain className="text-pink-500" />,
      <FaPython className="text-yellow-500" />,
      <SiLangchain className="text-emerald-500" />,
    ],
  },
  {
    title: "Problem Solving & DSA",
    desc: "Algorithms as leverage for correctness and performance.",
    points: ["Arrays, graphs, DP", "Complexity analysis", "1000+ problems solved"],
    icons: [
      <SiLeetcode className="text-yellow-500" />,
      <SiGeeksforgeeks className="text-green-600" />,
      <SiCodeforces className="text-sky-500" />,
      <SiCodechef className="text-amber-600" />,
    ],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const About = () => {
  return (
    <section name="About" className="aurora-section px-6 py-20">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 max-w-2xl"
        >
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white md:text-4xl">
            About <span className="accent-text">Me</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400">
            I'm a software engineer focused on building scalable systems,
            clean architectures, and production-ready applications — with
            strong foundations in system design, problem solving, and,
            more recently, applied AI engineering.
          </p>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, index) => (
            <motion.article
              key={card.title}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (index % 3) * 0.08 }}
              className="panel flex flex-col p-6"
            >
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                {card.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-zinc-400">
                {card.desc}
              </p>

              <ul className="mt-4 space-y-1.5">
                {card.points.map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-2 text-sm text-slate-600 dark:text-zinc-400"
                  >
                    <span className="h-1 w-1 rounded-full bg-indigo-500" />
                    {point}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4 text-xl dark:border-zinc-800">
                {card.icons.map((icon, idx) => (
                  <span key={idx}>{icon}</span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
